import { memo, useEffect, useContext, useCallback } from "react";
import { Button, Text, ScrollArea } from "@mantine/core";
import { MdDownload } from "react-icons/md";
import DashboardCard from "../../DashboardCard";
import { DEFAULT_ORDER, ROUTE_NAMES } from "src/utils/constants";
import { AppContext } from "src/api/AppContext";
import {
  GetAgreementReportPageDocument,
  GetAgreementReportPageQuery,
  GetAgreementReportPageQueryVariables,
  SubscriptionStatus,
  TransactionStatus,
  useDownloadAgreementCsvMutation,
} from "src/generated/graphql";
import { ArrayElement } from "src/utils/helperTypes";
import { FetchParams, QueryResponse } from "@kapex/tables";
import { useApolloClient } from "@apollo/client";
import ScrollableDataGrid from "src/components/ScrollableDataGrid";
import { downloadCSV, getFileNameCsv } from "src/utils/utils";
import { useDashboardContext } from "src/api/context/DashboardContext";

export type AgreementReportData = ArrayElement<
  GetAgreementReportPageQuery["agreementReportPage"]["items"]
>;

const AgreementReport = () => {
  const {
    filters,
    setFilters,
    getDateRangeText,
    handleModal,
    handleLocChange,
    paginationParams,
    setPaginationParams,
  } = useDashboardContext();
  const { query } = useApolloClient();
  const { setCurrentPageTitle } = useContext(AppContext);

  const [downloadAgreementCsv, { loading: isCsvDownloading }] =
    useDownloadAgreementCsvMutation();

  useEffect(() => {
    setCurrentPageTitle(ROUTE_NAMES.REPORT_AGREEMENT);
    return () => {
      setCurrentPageTitle(undefined);
    };
  }, []);

  const handleSaveAsCSV = async () => {
    if (!filters.dateRange?.[0]) return;
    const { dateRange, locations } = filters;
    const { data } = await downloadAgreementCsv({
      variables: {
        data: {
          startAt: dateRange[0],
          endAt: dateRange[1],
          locations: locations.map((e) => ({ id: e })),
        },
        pagination: paginationParams,
      },
    });

    downloadCSV(
      data?.downloadAgreementCSV,
      getFileNameCsv("Agreement_Report", filters.dateRange)
    );
  };

  const getReportPage = async (
    fetchParams: FetchParams
  ): Promise<QueryResponse<AgreementReportData>> => {
    const { dateRange, locations } = filters;
    if (!dateRange?.[0])
      return {
        rowData: [],
        rowCount: 0,
      };

    setPaginationParams({
      orderBy: fetchParams.orderBy,
      filters: fetchParams.filters,
    });

    let { data } = await query<
      GetAgreementReportPageQuery,
      GetAgreementReportPageQueryVariables
    >({
      query: GetAgreementReportPageDocument,
      variables: {
        pagination: {
          count: fetchParams.count,
          page: fetchParams.page,
          orderBy: fetchParams.orderBy || DEFAULT_ORDER.agreement_name,
          filters: fetchParams.filters,
        },
        data: {
          startAt: dateRange[0],
          endAt: dateRange[1],
          locations: locations.map((e) => ({ id: e })),
        },
      },
    });

    if (data.agreementReportPage) {
      return {
        rowCount: data.agreementReportPage.totalRows,
        rowData: data.agreementReportPage.items as AgreementReportData[],
      };
    }
    return {
      rowData: [],
      rowCount: 0,
    };
  };

  const ShowTable = useCallback(() => {
    if (!filters.dateRange?.[0]) return <></>;
    return (
      <ScrollableDataGrid<AgreementReportData>
        topMargin={122}
        columnConfigs={[
          {
            field: "agreement_name",
            name: "Agreement Name",
            filter: {
              field: "agreement.name",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ agreement }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {agreement?.name || "N/A"}
              </Text>
            ),
          },
          {
            field: "transaction_agreementId",
            name: "Agreement Id",
            filter: {
              field: "agreementId",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ agreementId }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {agreementId}
              </Text>
            ),
          },
          {
            field: "client_id",
            name: "Account Id",
            filter: {
              field: "client.id",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ subscription }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {subscription?.signedAgreement.client.id || "N/A"}
              </Text>
            ),
          },
          {
            field: "client_name",
            name: "Account Name",
            filter: {
              field: "client_name",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ subscription }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {subscription?.signedAgreement
                  ? `${subscription.signedAgreement.client.user.firstName} ${subscription.signedAgreement.client.user.lastName}`
                  : "N/A"}
              </Text>
            ),
          },
          {
            field: "agreement_isMembership",
            name: "isMembership",
            filter: {
              field: "agreement.isMembership",
              type: "select",
              options: ["true", "false"],
            },
            sortable: true,
            cellRenderer: ({ agreement }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {agreement?.isMembership ? "true" : "false"}
              </Text>
            ),
          },
          {
            field: "isRecurring",
            name: "isRecurring",
            filter: {
              field: "isRecurring",
              type: "select",
              options: ["true", "false"],
            },
            sortable: true,
            cellRenderer: ({ isRecurring }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {isRecurring ? "true" : "false"}
              </Text>
            ),
          },
          {
            field: "agreement_isAutoRenew",
            name: "isAutoRenew",
            filter: {
              field: "agreement.isAutoRenew",
              type: "select",
              options: ["true", "false"],
            },
            sortable: true,
            cellRenderer: ({ agreement }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {agreement?.isAutoRenew ? "true" : "false"}
              </Text>
            ),
          },
          {
            field: "signedAgreement_termStart",
            name: "Term Start",
            filter: {
              field: "signedAgreement.termStart",
              type: "dateRange",
            },
            sortable: true,
            valueParser: ({ subscription }) => {
              return subscription?.signedAgreement
                ? new Date(
                    subscription?.signedAgreement?.termStart
                  ).toDateString()
                : "N/A";
            },
          },
          {
            field: "signedAgreement_termEnd",
            name: "Term End",
            filter: {
              field: "signedAgreement.termEnd",
              type: "dateRange",
            },
            sortable: true,
            valueParser: ({ subscription }) => {
              return subscription?.signedAgreement
                ? new Date(
                    subscription?.signedAgreement?.termEnd
                  ).toDateString()
                : "N/A";
            },
          },
          {
            field: "subscription_startDate",
            name: "Subscription Start",
            filter: {
              field: "subscription.startDate",
              type: "dateRange",
            },
            sortable: true,
            valueParser: ({ subscription }) => {
              return subscription
                ? new Date(subscription.startDate).toDateString()
                : "N/A";
            },
          },
          {
            field: "subscription_endDate",
            name: "Subscription End",
            filter: {
              field: "subscription.endDate",
              type: "dateRange",
            },
            sortable: true,
            valueParser: ({ subscription }) => {
              return subscription
                ? new Date(subscription?.endDate).toDateString()
                : "N/A";
            },
          },
          {
            field: "nextDraftDate",
            name: "Next DraftDate",
            filter: {
              field: "subscription.nextDraftDate",
              type: "dateRange",
            },
            sortable: true,
            valueParser: ({ subscription }) => {
              return subscription
                ? new Date(subscription?.nextDraftDate).toDateString()
                : "N/A";
            },
          },
          {
            field: "subscriptionStatus",
            name: "Subscription Status",
            filter: {
              field: "subscription.subscriptionStatus",
              type: "select",
              options: Object.values(SubscriptionStatus),
            },
            sortable: true,
            cellRenderer: ({ subscription }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {subscription ? subscription.subscriptionStatus : "N/A"}
              </Text>
            ),
          },
          {
            field: "transactionStatus",
            name: "Transaction Status",
            filter: {
              field: "transactionStatus",
              type: "select",
              options: Object.values(TransactionStatus),
            },
            sortable: true,
            cellRenderer: ({ transactionStatus }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {transactionStatus}
              </Text>
            ),
          },
          {
            field: "transaction_createdDate",
            name: "Transaction createdDate",
            filter: {
              field: "createdDate",
              type: "dateRange",
            },
            sortable: true,
            valueParser: ({ createdDate }) => {
              return new Date(createdDate).toDateString();
            },
          },
          {
            field: "completedAt",
            name: "Completed date",
            filter: {
              field: "completedAt",
              type: "dateRange",
            },
            sortable: true,
            valueParser: ({ completedAt }) => {
              return completedAt ? new Date(completedAt).toDateString() : "N/A";
            },
          },
        ]}
        dataFetch={getReportPage}
      />
    );
  }, [filters]);

  return (
    <DashboardCard
      filters={filters}
      setFilters={setFilters}
      getDateRangeText={() => getDateRangeText(filters)}
      handleModal={() => handleModal(false)}
      handleLocChange={handleLocChange}
      isChartDetail={true}
    >
      <Button
        mb={"xs"}
        leftIcon={<MdDownload size={16} />}
        onClick={handleSaveAsCSV}
        loading={isCsvDownloading}
      >
        Download Csv
      </Button>
      <ScrollArea style={{ width: "100%" }}>
        <ShowTable />
      </ScrollArea>
    </DashboardCard>
  );
};

export default memo(AgreementReport);
